// Shared service data. Used by:
//   - ServicesSection (homepage card grid, summary view)
//   - ServicesPage (/services dedicated page, full breakdown)
//   - ContactPage (looks up tier + addons from URL params)
//
// All prices are excl. 21% VAT (Belgian B2B convention).
//
// `slug` matches the chip labels in ContactPage so /contact?service=<slug>
// pre-selects the right service chip on submit.
//
// Each tool object renders as a small monogram circle in the service title row
// on the /services page (avatar-group style).
//
// Each addon has an `indicative` price string for transparency on the contact
// form when the visitor picks add-ons.

// Tools render as logo chips on the /services page.
//   - `iconSlug` (when set) pulls the official brand SVG from simple-icons and
//     tints it with `brand`. Used for tools whose logos are public marks.
//   - `abbr` (when set) renders an Adobe-CC-style two-letter monogram on a
//     darker brand background. Adobe + Procreate marks were removed from
//     simple-icons due to brand guidelines, so we mirror Adobe's own
//     two-letter app-icon convention instead.
const TOOL = {
  Figma:        { name: 'Figma',         iconSlug: 'figma',      brand: '#F24E1E' },
  WordPress:    { name: 'WordPress',     iconSlug: 'wordpress',  brand: '#21759B' },
  Elementor:    { name: 'Elementor',     iconSlug: 'elementor',  brand: '#92003B' },
  ThreeJs:      { name: 'Three.js',      iconSlug: 'threedotjs', brand: '#000000' },
  React:        { name: 'React',         iconSlug: 'react',      brand: '#61DAFB' },
  Blender:      { name: 'Blender',       iconSlug: 'blender',    brand: '#E87D0D' },
  AfterEffects: { name: 'After Effects', abbr: 'Ae', bg: '#00005B', fg: '#D1A0FF' },
  ThreeDsMax:   { name: '3ds Max',       abbr: '3ds', bg: '#0696D7', fg: '#FFFFFF' },
  Illustrator:  { name: 'Illustrator',   abbr: 'Ai', bg: '#330000', fg: '#FF9A00' },
  Photoshop:    { name: 'Photoshop',     abbr: 'Ps', bg: '#001E36', fg: '#31A8FF' },
  InDesign:     { name: 'InDesign',      abbr: 'Id', bg: '#49021F', fg: '#F36' },
  Procreate:    { name: 'Procreate',     abbr: 'Pc', bg: '#000000', fg: '#FFFFFF' },
};

export const services = [
  {
    id: 1,
    title: "WEB DESIGN",
    slug: "Web design",
    icon: "🌐",
    cost: "from €1.800",
    outcome: "A fast, on-brand site you can actually run yourself.",
    bestIf: "You need a credible, conversion-ready web presence.",
    description:
      "Custom, responsive websites designed to convert local traffic into loyal clients. From fast-loading landing pages for real estate to full corporate platforms, I build digital experiences that drive real business growth.",
    longDescription:
      "From a sharp single-page site to a fully custom build with motion and 3D — every web project starts in Figma, gets shipped on WordPress (with Elementor) or as a fully custom React build, and lands with on-page SEO baked in. You get a site that's fast, accessible, and easy for you to update.",
    tools: [TOOL.Figma, TOOL.WordPress, TOOL.Elementor, TOOL.React, TOOL.ThreeJs],
    process: [
      "Discovery call & scope",
      "Wireframes + Figma design",
      "Build & integrations",
      "Launch + 30d support",
    ],
    tiers: [
      {
        name: "Starter",
        price: "€1.800 – €2.500",
        priceMin: 1800,
        priceMax: 2500,
        timeline: "1–2 weeks",
        bestFor: "One-pagers, freelancers, local businesses",
        features: [
          "Up to 5 pages, fully responsive",
          "On-page SEO + meta tags",
          "Contact form + Google Analytics",
          "1 round of revisions",
          "30 days post-launch support",
        ],
        notIncluded: [
          "No custom 3D WebGL scenes",
          "No custom CMS or advanced integrations",
          "No bespoke kinetic micro-interactions",
        ],
      },
      {
        name: "Standard",
        price: "€3.500 – €5.500",
        priceMin: 3500,
        priceMax: 5500,
        timeline: "3–5 weeks",
        bestFor: "Growing companies that need a CMS",
        features: [
          "Up to 10 pages with custom design",
          "WordPress + Elementor or fully custom React build",
          "Light interactions & animations",
          "Integrations (Stripe, Calendly, Mailchimp)",
          "2 rounds of revisions",
          "30 days post-launch support",
        ],
        notIncluded: [
          "No Three.js / WebGL 3D canvas (Premium only)",
          "No custom shader / VFX motion (Premium only)",
          "No Core Web Vitals 99+ deep tuning",
        ],
        featured: true,
      },
      {
        name: "Premium",
        price: "€7.500 +",
        priceMin: 7500,
        priceMax: 12000,
        timeline: "6–10+ weeks",
        bestFor: "Brands that want to wow on the first frame",
        features: [
          "Fully custom build, no templates",
          "Three.js / R3F 3D interactive scenes",
          "Bespoke micro-interactions & GSAP",
          "Performance audit & Core Web Vitals tuning",
          "3 rounds of revisions",
          "60 days post-launch support",
        ],
        notIncluded: [],
      },
    ],
    addons: [
      { label: "Copywriting per page",                 indicative: "+€80 / page",      addonMin: 80,  addonMax: 80  },
      { label: "Hosting & maintenance retainer",       indicative: "from €95 / mo",    addonMin: null, addonMax: null },
      { label: "Multilingual setup (per extra lang.)", indicative: "+€350 / language", addonMin: 350, addonMax: 350 },
    ],
  },
  {
    id: 2,
    title: "MOTION DESIGN",
    slug: "Motion design",
    icon: "🎞️",
    cost: "from €1.800",
    outcome: "Scroll-stopping motion & 3D that give your brand a heartbeat.",
    bestIf: "You want movement, 3D or VFX that makes people stop.",
    description:
      "Bring your brand to life with dynamic motion graphics. I create engaging social media loops, promotional campaign videos, and animated assets that capture attention and clearly explain your services to your audience.",
    longDescription:
      "From a clean logo animation to a full campaign film — animation, kinetic typography and 2D/3D motion built in After Effects, 3ds Max and Blender. 3D work lives here too: product motion, character pieces and scene-driven storytelling, finished with care for timing, sound-readiness and platform exports.",
    tools: [TOOL.AfterEffects, TOOL.Blender, TOOL.ThreeDsMax, TOOL.Photoshop, TOOL.Illustrator],
    process: [
      "Brief + moodboard",
      "Storyboard / kinetic frames",
      "Animation pass + look-dev",
      "Final render & exports",
    ],
    tiers: [
      {
        name: "Starter",
        price: "€1.800 – €2.800",
        priceMin: 1800,
        priceMax: 2800,
        timeline: "1–2 weeks",
        bestFor: "Logo animations, kinetic type, light edits on existing footage",
        features: [
          "Animated logo (3–5s)",
          "Light motion edits on existing video / audio",
          "Kinetic type or simple title cards",
          "Multiple aspect ratios (square, vertical, wide)",
          "1 round of revisions",
        ],
      },
      {
        name: "Campaign motion",
        price: "€3.500 – €6.500",
        priceMin: 3500,
        priceMax: 6500,
        timeline: "3–5 weeks",
        bestFor: "Brand films, product motion, kinetic type pieces",
        features: [
          "Up to 30s animated piece",
          "2D/3D mix with motion graphics",
          "Storyboard + 2 design frames",
          "2 rounds of revisions",
          "Color grade + finishing",
        ],
        featured: true,
      },
      {
        name: "3D motion",
        price: "€7.500 +",
        priceMin: 7500,
        priceMax: 12000,
        timeline: "6–10+ weeks",
        bestFor: "Full 3D scenes, character / product pieces, launch films",
        features: [
          "Full 3D scene built in 3ds Max / Blender",
          "Look-dev, lighting & materials",
          "Custom rigging or product motion",
          "Motion graphics + typography",
          "Color grade + finishing",
        ],
      },
    ],
    addons: [
      { label: "Royalty-free music + sound mix",                indicative: "+€150",         addonMin: 150, addonMax: 150 },
      { label: "Print-ready stills from scene",                 indicative: "+€150",         addonMin: 150, addonMax: 150 },
      { label: "Extra aspect ratios (TikTok / Reels / Shorts)", indicative: "+€120",         addonMin: 120, addonMax: 120 },
      { label: "Extra revision round",                          indicative: "+€180",         addonMin: 180, addonMax: 180 },
    ],
  },
  {
    id: 3,
    title: "BRANDING",
    slug: "Branding",
    icon: "🎨",
    cost: "from €1.500",
    outcome: "A complete identity system that stays consistent everywhere.",
    bestIf: "You're launching or relaunching and need a full system.",
    description:
      "Full brand systems and guidelines — type, colour, voice and applications — so every touchpoint stays consistent beyond the logo.",
    longDescription:
      "Branding goes far beyond a logo. The full system — type, color, voice, photography direction, and a guideline that makes sure everything stays consistent whether it's a billboard, an Instagram post, or a business card.",
    tools: [TOOL.Figma, TOOL.Illustrator, TOOL.Photoshop, TOOL.InDesign, TOOL.Procreate],
    process: [
      "Discovery + competitor mapping",
      "Visual direction & moodboard",
      "Identity design + applications",
      "Guideline delivery",
    ],
    tiers: [
      {
        name: "Mini",
        price: "€1.500 – €2.000",
        priceMin: 1500,
        priceMax: 2000,
        timeline: "2–3 weeks",
        bestFor: "Side projects, side hustles, small brands",
        features: [
          "Logo + 2 variations",
          "Color & type system (hex codes, fonts, weights)",
          "1-page brand usage PDF (do/don'ts)",
          "Social profile templates (IG post, IG story)",
        ],
      },
      {
        name: "Standard",
        price: "€2.500 – €3.500",
        priceMin: 2500,
        priceMax: 3500,
        timeline: "4–6 weeks",
        bestFor: "Startups, scale-ups, established SMEs",
        features: [
          "Logo + variations (primary, secondary, mono)",
          "Full color & type system",
          "Brand guideline PDF (15+ pages)",
          "Business card + letterhead design",
          "Social templates kit (5 formats)",
          "2 rounds of revisions",
        ],
        featured: true,
      },
      {
        name: "Comprehensive",
        price: "€5.500 +",
        priceMin: 5500,
        priceMax: 9000,
        timeline: "7–10+ weeks",
        bestFor: "Brands relaunching with weight",
        features: [
          "Logo system (primary, secondary, mono, icon)",
          "Full color & type system",
          "Brand guideline PDF (25+ pages, do/don'ts, applications)",
          "Naming + brand strategy support",
          "Tone of voice & messaging guide",
          "Print collateral (business cards, signage, pitch deck template)",
          "Photography & illustration style guide",
          "Animated logo bumper (3–5s intro/outro for video)",
        ],
      },
    ],
    addons: [
      { label: "Naming workshop",                   indicative: "+€450",         addonMin: 450, addonMax: 450 },
      { label: "Pitch deck template",               indicative: "+€350",         addonMin: 350, addonMax: 350 },
      { label: "Print management (cards, signage)", indicative: "Cost + 15%",    addonMin: null, addonMax: null },
      { label: "Brand voice & messaging guide",     indicative: "+€500",         addonMin: 500, addonMax: 500 },
    ],
  },
  {
    id: 4,
    title: "LOGO DESIGN",
    slug: "Logo design",
    icon: "✨",
    cost: "from €450",
    outcome: "A distinctive mark in every format you'll ever need.",
    bestIf: "You just need one sharp, versatile mark — fast.",
    description:
      "Stand out in the market with a strong, memorable brand identity. I deliver complete visual systems—from distinctive logo design to full corporate styling—ensuring your business makes a lasting, professional impression.",
    longDescription:
      "A focused branding & logo project — fast, no fluff. Options explored on paper before they hit the screen, then refined together until the mark and supporting styling feel right. For full systems and multi-year guidelines, see the Branding service.",
    tools: [TOOL.Illustrator, TOOL.Photoshop, TOOL.Figma, TOOL.InDesign],
    process: [
      "Brief & inspiration call",
      "3 concept directions",
      "Refinement of chosen direction",
      "Final delivery in all formats",
    ],
    tiers: [
      {
        name: "Logo only",
        price: "€450 – €700",
        priceMin: 450,
        priceMax: 700,
        timeline: "1 week",
        bestFor: "Quick launches, MVPs",
        features: [
          "3 unique concepts",
          "2 rounds of revisions",
          "Final logo in SVG, PNG, PDF",
          "Light + dark variants",
        ],
      },
      {
        name: "Logo + mini brand",
        price: "€900 – €1.300",
        priceMin: 900,
        priceMax: 1300,
        timeline: "2 weeks",
        bestFor: "Companies that need basics in place",
        features: [
          "3 unique logo concepts",
          "2 rounds of revisions",
          "Final logo in SVG, PNG, PDF",
          "Light + dark variants",
          "Color palette + typography",
          "1-page brand mini-guide (logo, palette, type, 2–3 do/don'ts)",
          "Email signature",
        ],
        featured: true,
      },
      {
        name: "Logo system",
        price: "€1.500 +",
        priceMin: 1500,
        priceMax: 2400,
        timeline: "3 weeks",
        bestFor: "Brands that need flexibility",
        features: [
          "3 unique logo concepts + 2 rounds of revisions",
          "Primary + secondary marks",
          "Wordmark + monogram + icon",
          "Light + dark variants, all formats (SVG, PNG, PDF)",
          "Animated logo bumper (3–5s intro/outro for video)",
          "Usage do's & don'ts guide",
        ],
      },
    ],
    addons: [
      { label: "Animated logo (3s bumper)", indicative: "+€350",         addonMin: 350, addonMax: 350 },
      { label: "Business card design",      indicative: "+€120 – €180",  addonMin: 120, addonMax: 180 },
      { label: "Email signature design",    indicative: "+€80",          addonMin: 80,  addonMax: 80  },
      { label: "Brand mini-guide (PDF)",    indicative: "+€250",         addonMin: 250, addonMax: 250 },
    ],
  },
];

export const getServiceBySlug = (slug) =>
  services.find((s) => s.slug === slug) || null;

export const getTierByName = (service, tierName) => {
  if (!service || !tierName) return null;
  return service.tiers.find((t) => t.name === tierName) || null;
};

export const getAddonByLabel = (service, label) => {
  if (!service || !label) return null;
  return service.addons.find((a) => a.label === label) || null;
};

// Set-diff between two tiers from the same service. Powers the "Switch tier"
// comparison panel on the contact page so visitors immediately see what they
// gain (extra features, more time) or lose (features cut, tighter timeline)
// when moving up or down a tier.
export const diffTiers = (from, to) => {
  if (!from || !to) {
    return {
      gained: [],
      lost: [],
      priceDelta: { min: 0, max: 0 },
    };
  }
  const fromFeatures = from.features || [];
  const toFeatures = to.features || [];
  return {
    gained: toFeatures.filter((f) => !fromFeatures.includes(f)),
    lost: fromFeatures.filter((f) => !toFeatures.includes(f)),
    priceDelta: {
      min: (to.priceMin || 0) - (from.priceMin || 0),
      max: (to.priceMax || 0) - (from.priceMax || 0),
    },
  };
};

// Maps legacy service slugs (from older shared URLs) onto current slugs so a
// /contact?service=3D / VFX link still prefills the right chip.
export const SERVICE_SLUG_ALIASES = {
  '3D / VFX': 'Motion design',
};

export const resolveServiceSlug = (slug) =>
  SERVICE_SLUG_ALIASES[slug] || slug;

// Best-effort numeric range parser for tier prices. Tiers also expose
// priceMin/priceMax directly — this is the fallback for older entries.
export const parsePriceRange = (price) => {
  if (!price || typeof price !== 'string') return { min: 0, max: 0 };
  const nums = price
    .replace(/\u2013|\u2014/g, '-')
    .match(/\d[\d.,]*/g);
  if (!nums || nums.length === 0) return { min: 0, max: 0 };
  const parsed = nums.map((n) => parseInt(n.replace(/[.,]/g, ''), 10) || 0);
  if (parsed.length === 1) {
    return { min: parsed[0], max: Math.round(parsed[0] * 1.5) };
  }
  return { min: parsed[0], max: parsed[1] };
};

const tierRange = (tier) => {
  if (!tier) return { min: 0, max: 0 };
  if (typeof tier.priceMin === 'number' && typeof tier.priceMax === 'number') {
    return { min: tier.priceMin, max: tier.priceMax };
  }
  return parsePriceRange(tier.price);
};

const addonRange = (addon) => {
  if (!addon) return { min: 0, max: 0, open: true };
  const hasNumeric =
    typeof addon.addonMin === 'number' && typeof addon.addonMax === 'number';
  if (!hasNumeric) return { min: 0, max: 0, open: true };
  return { min: addon.addonMin, max: addon.addonMax, open: false };
};

// Computes a min/max rough estimate for the active selection on the contact
// form. Every chosen package (tier) is summed so visitors can combine services
// (e.g. Branding + Web design). Services chosen as loose chips without a package
// fall back to their cheapest tier. Addons add on top. "Custom quote" addons
// (no numeric range) flag the result with hasOpenItems so the UI can show a
// "+ items on quote" disclaimer instead of guessing.
//
// Accepts either a `tiers` array of { service, tier } (new multi-package shape)
// or the legacy single `tier`/`service` pair.
export const computeQuoteEstimate = ({ tier, service, tiers = [], services: serviceSlugs = [], addons = [] }) => {
  let min = 0;
  let max = 0;
  let hasOpenItems = false;

  const tierPairs = tiers.length > 0 ? tiers : tier ? [{ service, tier }] : [];
  const slugsWithTier = new Set(tierPairs.map((p) => p.service?.slug).filter(Boolean));

  // Sum every chosen package.
  tierPairs.forEach(({ tier: t }) => {
    if (!t) return;
    const r = tierRange(t);
    min += r.min;
    max += r.max;
  });

  // Loose service chips (no package picked) fall back to their cheapest tier so
  // the running total still accounts for them.
  serviceSlugs.forEach((slug) => {
    if (slugsWithTier.has(slug)) return;
    const svc = getServiceBySlug(slug);
    if (!svc || !svc.tiers || svc.tiers.length === 0) return;
    const cheapest = svc.tiers.reduce((acc, t) => {
      const r = tierRange(t);
      if (!acc || r.min < acc.min) return r;
      return acc;
    }, null);
    if (cheapest) {
      min += cheapest.min;
      max += cheapest.max;
    }
  });

  addons.forEach((label) => {
    let resolved = null;
    if (service) {
      resolved = getAddonByLabel(service, label);
    }
    if (!resolved) {
      for (const slug of serviceSlugs) {
        const svc = getServiceBySlug(slug);
        const a = getAddonByLabel(svc, label);
        if (a) { resolved = a; break; }
      }
    }
    const r = addonRange(resolved);
    if (r.open) {
      hasOpenItems = true;
      return;
    }
    min += r.min;
    max += r.max;
  });

  return { min, max, hasOpenItems };
};

const formatThousand = (n) => {
  if (typeof n !== 'number' || Number.isNaN(n)) return '0';
  return n.toLocaleString('nl-BE').replace(/,/g, '.');
};

export const formatEuro = (n) => `\u20ac${formatThousand(n)}`;
